/**
 * 非齐次泊松到达生成器（设计文档 3.3 / traffic/passenger-gen.ts）。
 *
 * 段内到达间隔按指数分布采样：t += -ln(1 − rng.next()) / λ；
 * 所有随机数取自命名流（'arrival' 控制到达时刻，'od' 控制起讫楼层），互不扰动（AC-F006-02）。
 */
import type { ExperimentConfig, SceneConfig, TrafficMode } from '../config-schema';
import { SeededRng } from '../rng';
import { sampleOD } from './od-matrix';

export interface GeneratedPassenger {
  id: number;
  arrivalMs: number;
  originFloor: number;
  destFloor: number;
}

export function generatePassengers(
  scene: SceneConfig,
  profile: ExperimentConfig['traffic']['profile'],
  mode: TrafficMode,
  seed: number,
): GeneratedPassenger[] {
  const rng = new SeededRng(seed);
  const arrival = rng.stream('arrival');
  const od = rng.stream('od');

  const out: GeneratedPassenger[] = [];
  let id = 0;

  for (const seg of profile) {
    const startMs = seg.startSec * 1000;
    const endMs = seg.endSec * 1000;
    const ratePerSec = seg.arrivalRatePer5min / 300; // 人/秒
    if (ratePerSec <= 0) continue;

    let t = startMs; // 浮点毫秒，保持到达率精度
    while (t < endMs) {
      t += arrival.exponential(ratePerSec) * 1000;
      if (t < endMs) {
        const { origin, dest } = sampleOD(scene, mode, od);
        out.push({ id: id++, arrivalMs: Math.round(t), originFloor: origin, destFloor: dest });
      }
    }
  }

  // 时段内已按时刻递增；跨时段统一排序保证入队即有序（同时刻按 id）
  out.sort((a, b) => a.arrivalMs - b.arrivalMs || a.id - b.id);
  return out;
}
